import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminEmployeesSection from './AdminEmployeesSection';
import AdminAgreementsSection from './AdminAgreementsSection';
import './AdminDashboard.css';

function normId(item) {
  if (!item) return null;
  const id = item.id ?? item._id;
  if (id == null) return null;
  if (typeof id === 'object' && id?.$oid) return id.$oid;
  return String(id);
}

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

function filterAndSort(items, filterText, sortKey, sortDir, textFields) {
  let list = [...(items || [])];
  if (filterText.trim()) {
    const q = filterText.toLowerCase().trim();
    list = list.filter(item =>
      textFields.some(f => (item[f] || '').toLowerCase().includes(q))
    );
  }
  if (sortKey && sortDir) {
    list.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }
  return list;
}

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [useFileUpload, setUseFileUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDownloadFile, setSelectedDownloadFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const [serviceForm, setServiceForm] = useState({ title: '', category: '', description: '', image_url: '', details: '' });
  const navigate = useNavigate();
  const toast = useToast();

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, galleryRes, downloadsRes, inquiriesRes, settingsRes] = await Promise.all([
        api.get('/services'),
        api.get('/gallery'),
        api.get('/downloads'),
        api.get('/inquiries'),
        api.get('/settings').catch(() => ({ data: null }))
      ]);
      setServices(servicesRes.data);
      setGalleryImages(galleryRes.data);
      setDownloads(downloadsRes.data);
      setInquiries(inquiriesRes.data);
      setSettings(settingsRes?.data || {
        popup_enabled: true,
        popup_initial_delay_seconds: 8,
        popup_increment_seconds: 2,
        animation_enabled: true,
        intro_video_enabled: true,
        intro_video_url: ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } else {
        console.error('Error fetching data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'services' && showForm) {
      setServiceForm({
        title: editingItem?.title || '',
        category: editingItem?.category || '',
        description: editingItem?.description || '',
        image_url: editingItem?.image_url || '',
        details: editingItem?.details || ''
      });
    }
  }, [activeTab, showForm, editingItem]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleSettingsChange = (key, value) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : null));
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    if (!settings) return;
    setSavingSettings(true);
    try {
      await api.patch('/settings', {
        popup_enabled: settings.popup_enabled,
        popup_initial_delay_seconds: Number(settings.popup_initial_delay_seconds) || 8,
        popup_increment_seconds: Number(settings.popup_increment_seconds) || 2,
        animation_enabled: settings.animation_enabled,
        intro_video_enabled: settings.intro_video_enabled,
        intro_video_url: (settings.intro_video_url || '').trim()
      });
      const res = await api.get('/settings');
      setSettings(res.data);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    const id = typeof item === 'object' ? normId(item) : item;
    if (!id) {
      toast.error('Invalid item ID.');
      return;
    }
    try {
      await api.delete(`/${type}/${id}`);
      fetchData();
      toast.success('Item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDownloadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedDownloadFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      
      const response = await api.post('/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploading(false);
      return response.data.image_url;
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleFileUpload = async () => {
    if (!selectedDownloadFile) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedDownloadFile);
      
      const response = await api.post('/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploading(false);
      return response.data.file_url;
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    let data;
    if (type === 'services') {
      data = { ...serviceForm };
      if (useImageUpload && selectedFile) {
        const imageUrl = await handleImageUpload();
        if (imageUrl) {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
          data.image_url = `${baseUrl}${imageUrl}`;
        }
      }
    } else {
      const formData = new FormData(e.target);
      data = Object.fromEntries(formData);
      if (type === 'gallery_images' && useImageUpload && selectedFile) {
        const imageUrl = await handleImageUpload();
        if (imageUrl) {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
          data.image_url = `${baseUrl}${imageUrl}`;
        }
      }
      if (type === 'downloads' && useFileUpload && selectedDownloadFile) {
        const fileUrl = await handleFileUpload();
        if (fileUrl) {
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
          data.file_url = `${baseUrl}${fileUrl}`;
        }
      }
    }

    try {
      if (editingItem) {
        const itemId = normId(editingItem);
        if (!itemId) {
          toast.error('Invalid item ID. Please refresh and try again.');
          return;
        }
        await api.put(`/${type}/${itemId}`, { [type.slice(0, -1)]: data });
      } else {
        await api.post(`/${type}`, { [type.slice(0, -1)]: data });
      }
      const wasEdit = !!editingItem;
      setShowForm(false);
      setEditingItem(null);
      setUseImageUpload(false);
      setUseFileUpload(false);
      setSelectedFile(null);
      setSelectedDownloadFile(null);
      setImagePreview(null);
      fetchData();
      e.target.reset();
      toast.success(wasEdit ? 'Item updated successfully' : 'Item added successfully');
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <a href="/" className="btn-link">View Site</a>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>

      <div className="dashboard-tabs">
        <button
          className={activeTab === 'services' ? 'active' : ''}
          onClick={() => { 
          setActiveTab('services'); 
          setShowForm(false); 
          setEditingItem(null);
          setUseImageUpload(false);
          setSelectedFile(null);
          setImagePreview(null);
        }}
        >
          Services
        </button>
        <button
          className={activeTab === 'gallery_images' ? 'active' : ''}
          onClick={() => { 
          setActiveTab('gallery_images'); 
          setShowForm(false); 
          setEditingItem(null);
          setUseImageUpload(false);
          setSelectedFile(null);
          setImagePreview(null);
        }}
        >
          Gallery
        </button>
        <button
          className={activeTab === 'downloads' ? 'active' : ''}
          onClick={() => { 
            setActiveTab('downloads'); 
            setShowForm(false); 
            setEditingItem(null);
            setUseFileUpload(false);
            setSelectedDownloadFile(null);
          }}
        >
          Downloads
        </button>
        <button
          className={activeTab === 'inquiries' ? 'active' : ''}
          onClick={() => { 
            setActiveTab('inquiries'); 
            setShowForm(false); 
            setEditingItem(null);
          }}
        >
          Inquiries
        </button>
        <button
          className={activeTab === 'employees' ? 'active' : ''}
          onClick={() => { setActiveTab('employees'); setShowForm(false); setEditingItem(null); }}
        >
          Management
        </button>
        <button
          className={activeTab === 'agreements' ? 'active' : ''}
          onClick={() => { setActiveTab('agreements'); setShowForm(false); setEditingItem(null); }}
        >
          Agreements
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => { setActiveTab('settings'); setShowForm(false); setEditingItem(null); }}
        >
          Settings
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'employees' ? (
          <AdminEmployeesSection />
        ) : activeTab === 'agreements' ? (
          <AdminAgreementsSection />
        ) : activeTab === 'settings' ? (
          <div className="settings-tab">
            <h2>Settings</h2>
            {settings && (
              <form onSubmit={handleSettingsSubmit} className="settings-form">
                <div className="settings-field settings-toggle">
                  <label>
                    <span>Enable Inquiry Popup</span>
                    <input
                      type="checkbox"
                      checked={settings.popup_enabled !== false}
                      onChange={(e) => handleSettingsChange('popup_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
                <div className="settings-field">
                  <label>Initial popup delay (seconds)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={settings.popup_initial_delay_seconds}
                    onChange={(e) => handleSettingsChange('popup_initial_delay_seconds', e.target.value)}
                  />
                </div>
                <div className="settings-field">
                  <label>Increment time after each close (seconds)</label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={settings.popup_increment_seconds}
                    onChange={(e) => handleSettingsChange('popup_increment_seconds', e.target.value)}
                  />
                </div>
                {/* <div className="settings-field settings-toggle">
                  <label>
                    <span>Enable Homepage Service Animation</span>
                    <input
                      type="checkbox"
                      checked={settings.animation_enabled !== false}
                      onChange={(e) => handleSettingsChange('animation_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div> */}
                <div className="settings-field settings-toggle">
                  <label>
                    <span>Enable intro video on homepage</span>
                    <input
                      type="checkbox"
                      checked={settings.intro_video_enabled !== false}
                      onChange={(e) => handleSettingsChange('intro_video_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
                {/* <div className="settings-field">
                  <label>Intro video URL (leave blank for default)</label>
                  <input
                    type="url"
                    placeholder="/videos/banner-intro.mp4"
                    value={settings.intro_video_url || ''}
                    onChange={(e) => handleSettingsChange('intro_video_url', e.target.value)}
                  />
                </div> */}
                <div className="settings-actions">
                  <button type="submit" disabled={savingSettings}>
                    {savingSettings ? 'Saving…' : 'Save Settings'}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : showForm ? (
          <div className={`form-container ${activeTab === 'services' ? 'form-with-preview' : ''}`}>
            <div className="form-main">
              <h2>{editingItem ? 'Edit' : 'Add'} {activeTab.replace('_', ' ')}</h2>
              <form onSubmit={(e) => handleSubmit(e, activeTab)}>
                {activeTab === 'services' && (
                  <>
                    <input
                      name="title"
                      placeholder="Title"
                      required
                      value={serviceForm.title}
                      onChange={(e) => setServiceForm(f => ({ ...f, title: e.target.value }))}
                    />
                    <input
                      name="category"
                      placeholder="Category"
                      required
                      value={serviceForm.category}
                      onChange={(e) => setServiceForm(f => ({ ...f, category: e.target.value }))}
                    />
                    
                    <div className="image-upload-section">
                      <div className="upload-toggle">
                        <label>
                          <input 
                            type="checkbox" 
                            checked={useImageUpload}
                            onChange={(e) => {
                              setUseImageUpload(e.target.checked);
                              if (!e.target.checked) {
                                setSelectedFile(null);
                                setImagePreview(null);
                              }
                            }}
                          />
                          Use File Upload (instead of URL)
                        </label>
                      </div>
                      
                      {useImageUpload ? (
                        <div className="file-upload-area">
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={handleFileChange}
                            className="file-input"
                          />
                          {imagePreview && (
                            <div className="image-preview">
                              <img src={imagePreview} alt="Preview" />
                            </div>
                          )}
                          {uploading && <p className="upload-status">Uploading...</p>}
                        </div>
                      ) : (
                        <input 
                          name="image_url" 
                          type="url" 
                          placeholder="Image URL" 
                          value={serviceForm.image_url}
                          onChange={(e) => setServiceForm(f => ({ ...f, image_url: e.target.value }))}
                        />
                      )}
                    </div>
                    
                    <textarea
                      name="description"
                      placeholder="Short description (SEO meta description)"
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm(f => ({ ...f, description: e.target.value }))}
                      rows={3}
                    />
                    <label className="form-label">Details (WYSIWYG – headings, lists, links for strong SEO)</label>
                    <div className="quill-wrapper">
                      <ReactQuill
                        theme="snow"
                        value={serviceForm.details}
                        onChange={(v) => setServiceForm(f => ({ ...f, details: v }))}
                        modules={QUILL_MODULES}
                        placeholder="Add detailed content with headings (H2, H3), paragraphs, lists, links. Good for SEO."
                        className="service-details-editor"
                      />
                    </div>
                  </>
                )}
              {activeTab === 'gallery_images' && (
                <>
                  <input name="title" placeholder="Title" required defaultValue={editingItem?.title} />
                  
                  <div className="image-upload-section">
                    <div className="upload-toggle">
                      <label>
                        <input 
                          type="checkbox" 
                          checked={useImageUpload}
                          onChange={(e) => {
                            setUseImageUpload(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedFile(null);
                              setImagePreview(null);
                            }
                          }}
                        />
                        Use File Upload (instead of URL)
                      </label>
                    </div>
                    
                    {useImageUpload ? (
                      <div className="file-upload-area">
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={handleFileChange}
                          className="file-input"
                          required={!editingItem}
                        />
                        {imagePreview && (
                          <div className="image-preview">
                            <img src={imagePreview} alt="Preview" />
                          </div>
                        )}
                        {uploading && <p className="upload-status">Uploading...</p>}
                      </div>
                    ) : (
                      <input 
                        name="image_url" 
                        type="url" 
                        placeholder="Image URL" 
                        required={!useImageUpload}
                        defaultValue={editingItem?.image_url} 
                      />
                    )}
                  </div>
                  
                  <textarea name="description" placeholder="Description" defaultValue={editingItem?.description} />
                </>
              )}
              {activeTab === 'downloads' && (
                <>
                  <input name="title" placeholder="Title" required defaultValue={editingItem?.title} />
                  
                  <div className="image-upload-section">
                    <div className="upload-toggle">
                      <label>
                        <input 
                          type="checkbox" 
                          checked={useFileUpload}
                          onChange={(e) => {
                            setUseFileUpload(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedDownloadFile(null);
                            }
                          }}
                        />
                        Use File Upload (instead of URL)
                      </label>
                    </div>
                    
                    {useFileUpload ? (
                      <div className="file-upload-area">
                        <input 
                          type="file" 
                          onChange={handleDownloadFileChange}
                          className="file-input"
                          required={!editingItem}
                        />
                        {selectedDownloadFile && (
                          <div className="file-info">
                            <p>Selected file: {selectedDownloadFile.name}</p>
                            <p>Size: {(selectedDownloadFile.size / 1024).toFixed(2)} KB</p>
                          </div>
                        )}
                        {uploading && <p className="upload-status">Uploading...</p>}
                      </div>
                    ) : (
                      <input 
                        name="file_url" 
                        type="url" 
                        placeholder="File URL" 
                        required={!useFileUpload}
                        defaultValue={editingItem?.file_url} 
                      />
                    )}
                  </div>
                  
                  <textarea name="description" placeholder="Description" defaultValue={editingItem?.description} />
                </>
              )}
              <div className="form-actions">
                <button type="submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Save'}
                </button>
                <button type="button" onClick={() => { 
                  setShowForm(false); 
                  setEditingItem(null);
                  setUseImageUpload(false);
                  setUseFileUpload(false);
                  setSelectedFile(null);
                  setSelectedDownloadFile(null);
                  setImagePreview(null);
                }}>Cancel</button>
              </div>
            </form>
            </div>
            {activeTab === 'services' && (
              <div className="service-preview-pane">
                <h3>Live Preview</h3>
                <div className="service-preview">
                  <header className="service-preview-hero">
                    <div className="service-preview-breadcrumb">Services / {serviceForm.title || 'Service Title'}</div>
                    <h1>{serviceForm.title || 'Service Title'} in Ahmedabad</h1>
                    {serviceForm.description && <p className="service-preview-intro">{serviceForm.description}</p>}
                  </header>
                  <div className="service-preview-body">
                    {(serviceForm.image_url || imagePreview) && (
                      <div className="service-preview-image">
                        <img src={imagePreview || serviceForm.image_url} alt={serviceForm.title} />
                      </div>
                    )}
                    <div
                      className="service-preview-content wysiwyg"
                      dangerouslySetInnerHTML={{ __html: serviceForm.details || '<p class="ql-align-center"><em>Details will appear here as you type</em></p>' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="list-header">
              <h2>{activeTab.replace('_', ' ')}</h2>
              {activeTab !== 'inquiries' && activeTab !== 'settings' && (
                <button onClick={() => setShowForm(true)} className="btn-add">+ Add New</button>
              )}
            </div>
            {['services', 'gallery_images', 'downloads'].includes(activeTab) && (
              <div className="table-toolbar">
                <input
                  type="text"
                  placeholder="Filter..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="table-filter-input"
                />
              </div>
            )}
            <div className="items-list">
              {activeTab === 'services' && (() => {
                const list = filterAndSort(services, filterText, sortConfig.key, sortConfig.dir, ['title', 'category', 'description']);
                return (
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'title', dir: s.key === 'title' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Title {sortConfig.key === 'title' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'category', dir: s.key === 'category' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Category {sortConfig.key === 'category' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="hide-on-mobile">Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr><td colSpan={5} className="empty-row">{services.length === 0 ? 'No services yet. Click "+ Add New" to add one.' : 'No matches for filter.'}</td></tr>
                    ) : list.map(item => (
                      <tr key={item.id}>
                        <td>
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.title} className="table-image" />
                          ) : (
                            <span className="table-image-placeholder">—</span>
                          )}
                        </td>
                        <td><strong>{item.title}</strong></td>
                        <td>{item.category}</td>
                        <td className="table-desc hide-on-mobile" title={item.description}>{item.description || '—'}</td>
                        <td>
                          <div className="item-actions item-actions-icons">
                            <button type="button" className="btn-action btn-edit" onClick={() => { setEditingItem(item); setShowForm(true); setUseImageUpload(false); setSelectedFile(null); setImagePreview(null); }} aria-label="Edit"><EditIcon /><span className="btn-action-text">Edit</span></button>
                            <button type="button" className="btn-action btn-delete" onClick={() => handleDelete('services', item)} aria-label="Delete"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ); })()}
              {activeTab === 'gallery_images' && (() => {
                const list = filterAndSort(galleryImages, filterText, sortConfig.key, sortConfig.dir, ['title', 'description']);
                return (
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'title', dir: s.key === 'title' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Title {sortConfig.key === 'title' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="hide-on-mobile">Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr><td colSpan={4} className="empty-row">{galleryImages.length === 0 ? 'No gallery images yet. Click "+ Add New" to add one.' : 'No matches for filter.'}</td></tr>
                    ) : list.map(item => (
                      <tr key={item.id}>
                        <td>
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.title} className="table-image" />
                          ) : (
                            <span className="table-image-placeholder">—</span>
                          )}
                        </td>
                        <td><strong>{item.title}</strong></td>
                        <td className="table-desc hide-on-mobile" title={item.description}>{item.description || '—'}</td>
                        <td>
                          <div className="item-actions item-actions-icons">
                            <button type="button" className="btn-action btn-edit" onClick={() => { setEditingItem(item); setShowForm(true); setUseImageUpload(false); setSelectedFile(null); setImagePreview(null); }} aria-label="Edit"><EditIcon /><span className="btn-action-text">Edit</span></button>
                            <button type="button" className="btn-action btn-delete" onClick={() => handleDelete('gallery_images', item)} aria-label="Delete"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ); })()}
              {activeTab === 'downloads' && (() => {
                const list = filterAndSort(downloads, filterText, sortConfig.key, sortConfig.dir, ['title', 'description']);
                return (
                <table className="items-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'title', dir: s.key === 'title' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Title {sortConfig.key === 'title' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="hide-on-mobile">Description</th>
                      <th>File</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr><td colSpan={4} className="empty-row">{downloads.length === 0 ? 'No downloads yet. Click "+ Add New" to add one.' : 'No matches for filter.'}</td></tr>
                    ) : list.map(item => (
                      <tr key={item.id}>
                        <td><strong>{item.title}</strong></td>
                        <td className="table-desc hide-on-mobile" title={item.description}>{item.description || '—'}</td>
                        <td><a href={item.file_url} target="_blank" rel="noopener noreferrer">View File</a></td>
                        <td>
                          <div className="item-actions item-actions-icons">
                            <button type="button" className="btn-action btn-edit" onClick={() => { setEditingItem(item); setShowForm(true); setUseFileUpload(false); setSelectedDownloadFile(null); }} aria-label="Edit"><EditIcon /><span className="btn-action-text">Edit</span></button>
                            <button type="button" className="btn-action btn-delete" onClick={() => handleDelete('downloads', item)} aria-label="Delete"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ); })()}
              {activeTab === 'inquiries' && (
                inquiries.length === 0 ? (
                  <div className="empty-state">
                    <p>No inquiries yet. Inquiries submitted through the contact form will appear here.</p>
                  </div>
                ) : (
                  inquiries.map(item => (
                    <div key={item.id} className="item-card inquiry-card">
                      <div className="inquiry-header">
                        <h3>{item.subject}</h3>
                        <span className="inquiry-date">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p><strong>From:</strong> {item.full_name}</p>
                      <p><strong>Email:</strong> <a href={`mailto:${item.email}`}>{item.email}</a></p>
                      <div className="inquiry-comment">
                        <strong>Message:</strong>
                        <p>{item.comment}</p>
                      </div>
                      <div className="item-actions item-actions-icons">
                        <button type="button" className="btn-action btn-delete" onClick={() => handleDelete('inquiries', item)} aria-label="Delete"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
